import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { cpGetAuthHeader } from './ClientPortalApp';
import { openFilePreview } from '../common/FilePreviewModal';

const API = '/api/client/daily-reports/';

const formatDate = (v) => (v ? new Date(v).toLocaleDateString() : '-');

const formatReportTitle = (title) => (title ? title.replace(/(\d{4})-(\d{2})-(\d{2})$/, '$3-$2-$1') : '-');

const toISODate = (d) => {
    const dt = new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const DownloadIcon = ({ url, title, icon, fileType }) => {
    if (!url) return <span style={{ color: '#999' }}>-</span>;
    const handleClick = (e) => {
        e.stopPropagation();
        if (fileType === 'pdf') {
            openFilePreview(url, title);
        } else {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };
    return (
        <button
            className="cp-btn-icon"
            title={fileType === 'pdf' ? 'Download / View PDF' : 'Download Excel'}
            onClick={handleClick}
        >
            <i className={`fa-solid ${icon}`}></i>
        </button>
    );
};

const ReportDateCalendar = ({ selectedDate, onSelect, availableDates }) => {
    const availableSet = useMemo(() => new Set(availableDates), [availableDates]);
    const [viewDate, setViewDate] = useState(() => {
        if (selectedDate) return new Date(selectedDate + 'T00:00:00');
        return new Date();
    });

    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);

    const handlePrev = () => setViewDate(new Date(year, month - 1, 1));
    const handleNext = () => setViewDate(new Date(year, month + 1, 1));

    return (
        <div className="cp-report-calendar">
            <div className="cp-calendar-header">
                <button className="cp-calendar-nav" onClick={handlePrev} type="button"><i className="fa-solid fa-chevron-left"></i></button>
                <span>{new Date(year, month, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</span>
                <button className="cp-calendar-nav" onClick={handleNext} type="button"><i className="fa-solid fa-chevron-right"></i></button>
            </div>
            <div className="cp-calendar-grid">
                {['S','M','T','W','T','F','S'].map((d, i) => (
                    <div key={i} className="cp-calendar-day-header">{d}</div>
                ))}
                {days.map((d, i) => {
                    if (d === null) return <div key={i} className="cp-calendar-day empty" />;
                    const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                    const available = availableSet.has(iso);
                    const selected = selectedDate === iso;
                    return (
                        <button
                            key={i}
                            className={`cp-calendar-day${available ? ' available' : ''}${selected ? ' selected' : ''}`}
                            onClick={() => available && onSelect(iso)}
                            disabled={!available}
                            type="button"
                        >
                            {d}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const ClientDailyReports = () => {
    const [reports, setReports] = useState([]);
    const [activeTab, setActiveTab] = useState('today');
    const [selectedDate, setSelectedDate] = useState('');
    const [tableData, setTableData] = useState(null);
    const [loadingReports, setLoadingReports] = useState(false);
    const [loadingTable, setLoadingTable] = useState(false);

    const todayISO = useMemo(() => toISODate(new Date()), []);
    const todayReport = useMemo(() => reports.find(r => toISODate(r.report_date) === todayISO), [reports, todayISO]);
    const previousReport = useMemo(() => selectedDate ? reports.find(r => toISODate(r.report_date) === selectedDate) : null, [reports, selectedDate]);
    const activeReport = activeTab === 'today' ? todayReport : previousReport;

    const availableDates = useMemo(() => {
        const seen = new Set();
        return reports
            .map(r => {
                const d = toISODate(r.report_date);
                if (seen.has(d)) return null;
                seen.add(d);
                return d;
            })
            .filter(Boolean)
            .sort()
            .reverse();
    }, [reports]);

    const fetchReports = useCallback(async () => {
        setLoadingReports(true);
        try {
            const res = await fetch(API, { headers: cpGetAuthHeader() });
            const data = await res.json();
            const list = data.results || data || [];
            setReports(list);
        } catch {
            setReports([]);
        } finally {
            setLoadingReports(false);
        }
    }, []);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    useEffect(() => {
        if (!activeReport) {
            setTableData(null);
            return;
        }
        const fetchTable = async () => {
            setLoadingTable(true);
            try {
                const res = await fetch(`${API}${activeReport.id}/table/`, { headers: cpGetAuthHeader() });
                const data = await res.json();
                setTableData(data.groups || []);
            } catch {
                setTableData([]);
            } finally {
                setLoadingTable(false);
            }
        };
        fetchTable();
    }, [activeReport]);

    const renderReport = (report) => (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
                <div>
                    <div className="cp-section-title">{formatReportTitle(report.title)}</div>
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <DownloadIcon
                        url={report.pdf_url}
                        title={`Daily Report - ${formatDate(report.report_date)}`}
                        icon="fa-file-pdf"
                        fileType="pdf"
                    />
                    <DownloadIcon
                        url={report.excel_url}
                        title={`Daily Report - ${formatDate(report.report_date)}`}
                        icon="fa-file-excel"
                        fileType="excel"
                    />
                </div>
            </div>

            {loadingTable ? (
                <div className="cp-loader" style={{ padding: 16 }}>
                    <i className="fa-solid fa-circle-notch fa-spin"></i> Loading report...
                </div>
            ) : tableData && tableData.length > 0 ? (
                tableData.map((group, gIdx) => (
                    <div key={gIdx} style={{ marginBottom: 24 }}>
                        <div style={{
                            background: '#e5def1',
                            padding: '8px 12px',
                            borderRadius: '6px 6px 0 0',
                            fontWeight: 'bold',
                            fontSize: 14,
                            color: '#333'
                        }}>{group.shipment_type}</div>
                        <div className="cp-table-wrap" style={{ overflowX: 'auto' }}>
                            <table className="cp-table" style={{ minWidth: '100%' }}>
                                <thead>
                                    <tr>
                                        {group.headers.map((h, i) => (
                                            <th key={i} style={{ whiteSpace: 'nowrap' }}>{h || '-'}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {group.rows.map((row, rIdx) => (
                                        <tr key={rIdx}>
                                            {row.map((cell, cIdx) => (
                                                <td key={cIdx} style={{ whiteSpace: 'nowrap' }}>{cell}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ))
            ) : (
                <div className="cp-empty-cell">No table data found for this report.</div>
            )}
        </div>
    );

    return (
        <div className="cp-page" style={{ padding: '0 0 24px' }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 24, borderBottom: '2px solid #e0e0e0' }}>
                <button
                    className="cp-btn"
                    onClick={() => setActiveTab('today')}
                    style={{
                        padding: '10px 20px',
                        border: 'none',
                        borderBottom: activeTab === 'today' ? '3px solid #0d6efd' : '3px solid transparent',
                        background: activeTab === 'today' ? '#e7f1ff' : 'transparent',
                        color: activeTab === 'today' ? '#0d6efd' : '#444',
                        fontWeight: 600,
                        cursor: 'pointer',
                        borderRadius: '4px 4px 0 0'
                    }}
                >
                    Today's Report
                </button>
                <button
                    className="cp-btn"
                    onClick={() => setActiveTab('previous')}
                    style={{
                        padding: '10px 20px',
                        border: 'none',
                        borderBottom: activeTab === 'previous' ? '3px solid #0d6efd' : '3px solid transparent',
                        background: activeTab === 'previous' ? '#e7f1ff' : 'transparent',
                        color: activeTab === 'previous' ? '#0d6efd' : '#444',
                        fontWeight: 600,
                        cursor: 'pointer',
                        borderRadius: '4px 4px 0 0'
                    }}
                >
                    Previous Reports
                </button>
            </div>

            {loadingReports && (
                <div className="cp-loader" style={{ padding: 16 }}>
                    <i className="fa-solid fa-circle-notch fa-spin"></i> Loading...
                </div>
            )}

            {!loadingReports && (
                <div>
                    {activeTab === 'today' ? (
                        todayReport ? (
                            renderReport(todayReport)
                        ) : (
                            <div className="cp-empty-cell">No daily report has been generated for today yet.</div>
                        )
                    ) : (
                        <div>
                            <div style={{ marginBottom: 20 }}>
                                <div style={{ fontWeight: 600, marginBottom: 12 }}>Select Report Date:</div>
                                <ReportDateCalendar
                                    selectedDate={selectedDate}
                                    onSelect={setSelectedDate}
                                    availableDates={availableDates}
                                />
                            </div>
                            {selectedDate && previousReport ? (
                                renderReport(previousReport)
                            ) : selectedDate ? (
                                <div className="cp-empty-cell">No report found for {formatDate(selectedDate)}.</div>
                            ) : (
                                <div className="cp-empty-cell">Select a date to view a previous report.</div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ClientDailyReports;
